/**
 * Facturer et acheter à l'étranger, en micro-entreprise.
 *
 * TROIS QUESTIONS AVANT D'ÉDITER LA FACTURE, et pas une de moins : le client
 * est-il un professionnel ou un particulier, est-il établi dans l'Union
 * européenne ou en dehors, lui vendez-vous un bien ou une prestation de
 * services ? Les réponses ne changent pas le prix, elles changent les mentions
 * à porter et les déclarations à déposer.
 *
 * LA DGFiP NE RECENSE QUE TROIS SITUATIONS EUROPÉENNES, et le référentiel s'en
 * tient là :
 *   - les ACHATS de biens dans l'Union au-delà de 10 000 € par an ;
 *   - les PRESTATIONS DE SERVICES entre professionnels, dès le premier euro ;
 *   - les VENTES À DISTANCE de biens à des particuliers de l'Union au-delà de
 *     10 000 € par an, celle que presque toutes les pages oublient.
 *
 * DEUX AUTRES SITUATIONS SUIVENT LA RÈGLE GÉNÉRALE DU LIEU DES SERVICES
 * (BOI-TVA-CHAMP-20-50, § 10, relevé le 2 novembre 2026) : un service vendu à
 * un professionnel est imposable là où ce client est établi, un service vendu
 * à un particulier là où le prestataire est établi. D'où deux cas couverts :
 *   - le service vendu à une ENTREPRISE HORS DE L'UNION, sans TVA française,
 *     dont aucune source ne donne la mention exacte : le module le dit ;
 *   - le service vendu à un PARTICULIER DE L'UNION, imposable en France, donc
 *     sous la franchise, sauf les services électroniques, qui suivent le seuil
 *     de 10 000 € des ventes à distance.
 *
 * DEUX SITUATIONS NE SE CHIFFRENT PAS, et le module les renvoie au service des
 * impôts plutôt que d'inventer une règle : la vente de BIENS à un professionnel
 * d'un autre État membre, et tout le reste HORS de l'Union (achats, biens,
 * particuliers, dont certains services sortent du champ de la TVA française
 * par l'article 259 B du CGI).
 *
 * LE PIÈGE LE PLUS CHER EST À L'ACHAT, pas à la vente. Un abonnement logiciel
 * de 40 € par mois souscrit auprès d'un éditeur irlandais fait de vous le
 * redevable de la TVA française sur cet achat, et vous oblige à déposer une
 * déclaration de TVA alors même que vous êtes en franchise. C'est ce que porte
 * la réserve « en règle générale » de la DGFiP, que presque personne ne lit.
 *
 * LA DES N'EST PAS UNE DÉCLARATION DE TVA. C'est un état récapitulatif, déposé
 * sur le portail de la DOUANE et jamais sur impots.gouv.fr, pour les seuls mois
 * où une prestation européenne a été facturée.
 *
 * LA MENTION DE FRANCHISE SUIT LA DATE DE LA FACTURE : 293 B du CGI jusqu'au
 * 31 décembre 2026, L. 233-3 du CIBS ensuite. Le parcours passe la date du
 * jour, et vatMentions.js tient la règle et ses sources. « Autoliquidation »
 * ne cite aucun article et ne change pas.
 */

import { nettoyer } from "./documentText.js";
import { MENTION_AUTOLIQUIDATION, mentionFranchise } from "./vatMentions.js";

/** D'où viennent les règles, et quand elles ont été relevées. */
export const BAREME = {
    annee: "2026",
    releveLe: "2026-11-02",
    source: "DGFiP et BOFiP pour les mentions et le lieu des services, douane.gouv.fr pour la déclaration européenne de services",
};

/** Les seuils annuels, en euros, des deux situations qui en ont un. */
export const SEUIL_ACHATS_BIENS = 10000;
export const SEUIL_VENTES_A_DISTANCE = 10000;

/* Les mentions, celle de franchise comme celle d'autoliquidation et leurs
   variantes admises, vivent dans vatMentions.js : elles dépendent de la date
   de la facture, et le devis les partage. */

/** Le délai de dépôt de la déclaration européenne de services. */
export const DES_DELAI = "le 10e jour ouvrable du mois suivant";
export const DES_PORTAIL = "le portail de la douane";

/* --------------------------------------------------------------------- */
/* La saisie                                                              */
/* --------------------------------------------------------------------- */

/** Un montant annuel, accepté avec virgule ou point. */
export function montant(brut) {
    const t = nettoyer(String(brut ?? ""))
        .replace(/\s/g, "")
        .replace(",", ".");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,9}(\.\d{1,2})?$/.test(t)) return { valeur: 0, motif: "format" };
    return { valeur: Math.round(Number(t) * 100) / 100, motif: null };
}

/** Un montant écrit à la française, insécables ordinaires comprises. */
export function euros(valeur) {
    const chiffres = valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return `${chiffres.replace(/ /g, " ")} €`;
}

/* --------------------------------------------------------------------- */
/* Le diagnostic                                                          */
/* --------------------------------------------------------------------- */

/**
 * La situation, telle que les trois questions la déterminent.
 *
 * `sens` vaut "vends" ou "achete", `zone` vaut "ue" ou "horsUe", `client` vaut
 * "pro" ou "particulier", `nature` vaut "service" ou "bien".
 */
export function situation(reponses) {
    const r = reponses || {};
    if (!r.sens || !r.zone) return null;

    // HORS UNION : seul le service vendu à une entreprise suit une règle
    // générale publiée. Le reste se renvoie au service des impôts.
    if (r.zone === "horsUe") {
        if (r.sens === "achete") return { cas: "horsUe" };
        if (!r.client) return null;
        if (r.client === "particulier") return { cas: "horsUe" };
        if (!r.nature) return null;
        return { cas: r.nature === "service" ? "horsUeServicesPro" : "horsUe" };
    }

    if (r.sens === "achete") {
        if (!r.nature) return null;
        // Les services achetés dans l'Union n'ont aucun seuil ; les biens en
        // ont un, cumulé sur l'année civile.
        return { cas: r.nature === "service" ? "achatServices" : "achatBiens" };
    }

    if (!r.client) return null;
    if (r.client === "pro") {
        if (!r.nature) return null;
        // La vente de BIENS à un professionnel européen ne figure pas parmi les
        // trois situations recensées : elle sort du champ arbitré.
        return { cas: r.nature === "service" ? "venteServicesPro" : "venteBiensPro" };
    }
    if (!r.nature) return null;
    // Les ventes à distance visent les BIENS. Un service vendu à un
    // particulier européen est imposable en France, sous la franchise.
    return { cas: r.nature === "bien" ? "venteADistance" : "venteServicesParticulier" };
}

/** Les cas que le référentiel couvre, et ceux qu'il renvoie au SIE. */
export const CAS_COUVERTS = [
    "achatServices",
    "achatBiens",
    "venteServicesPro",
    "venteADistance",
    "venteServicesParticulier",
    "horsUeServicesPro",
];
export const CAS_RENVOYES = ["horsUe", "venteBiensPro"];

/** Les cas couverts où aucun numéro de TVA intracommunautaire n'est en jeu. */
export const CAS_SANS_NUMERO = ["venteServicesParticulier", "horsUeServicesPro"];

/** Le seuil annuel du cas, ou null quand il n'y en a pas. */
export function seuilDuCas(cas) {
    if (cas === "achatBiens") return SEUIL_ACHATS_BIENS;
    if (cas === "venteADistance") return SEUIL_VENTES_A_DISTANCE;
    return null;
}

/**
 * Le numéro de TVA intracommunautaire est-il exigé ?
 *
 * Dès le premier euro pour les prestations de services entre professionnels,
 * au-delà de 10 000 € par an pour les deux autres situations. Le cumul se
 * compte sur l'année civile, et c'est le franchissement qui déclenche.
 */
export function numeroExige(reponses) {
    const s = situation(reponses);
    if (!s || CAS_RENVOYES.includes(s.cas)) return null;
    if (CAS_SANS_NUMERO.includes(s.cas)) {
        return { exige: false, sansObjet: true, seuil: null, cumul: null, reste: null };
    }

    const seuil = seuilDuCas(s.cas);
    if (seuil === null) return { exige: true, seuil: null, cumul: null, reste: null };

    const cumul = montant((reponses || {}).cumulAnnuel);
    if (cumul.motif) return null;

    return {
        exige: cumul.valeur > seuil,
        seuil,
        cumul: cumul.valeur,
        reste: Math.max(0, Math.round((seuil - cumul.valeur) * 100) / 100),
    };
}

/**
 * Ce que la situation impose : mentions, déclarations, et qui paie la TVA,
 * pour une facture datée de `date` (AAAA-MM-JJ ou date ISO).
 *
 * Rend `null` sur un cas renvoyé au service des impôts : il n'y a rien à
 * annoncer, et annoncer quand même serait le vrai défaut.
 */
export function obligations(reponses, date) {
    const s = situation(reponses);
    if (!s || CAS_RENVOYES.includes(s.cas)) return null;
    const numero = numeroExige(reponses);
    if (!numero) return null;
    // La mention de franchise dépend de la date de la facture : sans date
    // lisible, mieux vaut ne rien annoncer que d'annoncer la mauvaise.
    const franchise = mentionFranchise(date);
    if (!franchise) return null;

    const commun = { cas: s.cas, numero };

    if (s.cas === "venteServicesPro") {
        return {
            ...commun,
            // La taxe est due chez le client, qui l'autoliquide.
            redevable: "client",
            mentions: [franchise, MENTION_AUTOLIQUIDATION],
            deuxNumeros: true,
            des: true,
            declarationDeTva: false,
        };
    }

    if (s.cas === "achatServices" || s.cas === "achatBiens") {
        return {
            ...commun,
            // À l'achat, c'est VOUS qui devenez redevable, franchise ou non.
            redevable: "vous",
            mentions: [],
            deuxNumeros: false,
            des: false,
            declarationDeTva: numero.exige,
        };
    }

    if (s.cas === "venteServicesParticulier") {
        return {
            ...commun,
            // Imposable en France : la franchise s'applique, comme pour un
            // client français. Les services électroniques font exception.
            redevable: "franchise",
            mentions: [franchise],
            deuxNumeros: false,
            des: false,
            declarationDeTva: false,
            servicesElectroniques: true,
        };
    }

    if (s.cas === "horsUeServicesPro") {
        return {
            ...commun,
            // Imposable chez le client : aucune TVA française. Aucune source
            // ne donne la mention exacte, on ne l'invente pas.
            redevable: "horsFrance",
            mentions: [],
            mentionAConfirmer: true,
            deuxNumeros: false,
            des: false,
            declarationDeTva: false,
        };
    }

    // Ventes à distance de biens à des particuliers de l'Union.
    return {
        ...commun,
        redevable: "vous",
        mentions: [franchise],
        deuxNumeros: false,
        des: false,
        declarationDeTva: false,
        guichetUnique: numero.exige,
    };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!r.sens) manques.push("sens");
    if (!r.zone) manques.push("zone");
    if (r.zone === "ue") {
        if (r.sens === "vends" && !r.client) manques.push("client");
        if (!r.nature) manques.push("nature");
    }
    if (r.zone === "horsUe" && r.sens === "vends") {
        if (!r.client) manques.push("client");
        else if (r.client === "pro" && !r.nature) manques.push("nature");
    }

    const s = situation(r);
    if (s && !CAS_RENVOYES.includes(s.cas) && seuilDuCas(s.cas) !== null) {
        const cumul = montant(r.cumulAnnuel);
        if (cumul.motif === "vide") manques.push("cumulAnnuel");
        else if (cumul.motif) invalides.push({ champ: "cumulAnnuel", motif: cumul.motif });
        else {
            const seuil = seuilDuCas(s.cas);
            // Approcher le seuil sans l'avoir franchi mérite d'être dit : le
            // cumul se compte sur l'année et une seule commande peut basculer.
            if (cumul.valeur <= seuil && cumul.valeur > seuil * 0.8) alertes.push("seuilProche");
        }
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Ce que le parcours annonce, ligne par ligne, pour une facture datée de `date`. */
export function recapitulatif(reponses, date) {
    const r = reponses || {};
    if (!valider(r).valide) return null;
    const o = obligations(r, date);
    if (!o) return null;

    const lignes = [
        [
            "Numéro de TVA intracommunautaire",
            o.numero.sansObjet
                ? o.cas === "horsUeServicesPro"
                    ? "sans objet : il ne vaut que dans l'Union"
                    : "pas nécessaire pour un particulier"
                : o.numero.seuil === null
                  ? "obligatoire dès le premier euro"
                  : o.numero.exige
                    ? `obligatoire : vous avez dépassé ${euros(o.numero.seuil)} sur l'année`
                    : `pas encore : ${euros(o.numero.reste)} avant le seuil de ${euros(o.numero.seuil)}`,
        ],
        [
            "Qui paie la TVA",
            o.redevable === "client"
                ? "votre client, dans son pays, par autoliquidation"
                : o.redevable === "franchise"
                  ? "personne : votre franchise s'applique, comme pour un client français"
                  : o.redevable === "horsFrance"
                    ? "aucune TVA française : le service est imposable dans le pays de votre client"
                    : "vous, en France, sur cet achat",
        ],
    ];

    if (o.mentionAConfirmer) {
        lignes.push(["Mention sur la facture", "à faire confirmer par votre service des impôts"]);
    }
    if (o.mentions.length) {
        lignes.push([
            o.mentions.length > 1 ? "Mentions sur la facture" : "Mention sur la facture",
            o.mentions.map((m) => `« ${m} »`).join(" et "),
        ]);
    }
    if (o.deuxNumeros) {
        lignes.push(["Numéros à porter", "le vôtre et celui de votre client"]);
    }
    if (o.des) {
        lignes.push(["Déclaration européenne de services", `${DES_DELAI}, sur ${DES_PORTAIL}`]);
    }
    if (o.declarationDeTva) {
        lignes.push(["Déclaration de TVA", "à déposer, malgré la franchise en base"]);
    }
    if (o.guichetUnique) {
        lignes.push(["Déclaration", "possible par le guichet unique de TVA"]);
    }

    return lignes;
}
